import React, { useState, useEffect } from 'react';
import { getAllJobs, deleteJob, getJobStatus, generateCode } from '../../../services/codegen.service';
import { useNavigate } from 'react-router-dom';
import { showSuccess, showError, showWarning } from '../../../services/notification.service';
import ConfirmDialog from '../../shared/ConfirmDialog';
import './CodeJobsList.css';

function CodeJobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', job: null, jobId: null });
  const navigate = useNavigate();

  const loadJobs = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await getAllJobs();
      if (response.success) {
        const sortedJobs = response.jobs.sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setJobs(sortedJobs);
        setError(null);
      } else {
        setError('Failed to load jobs');
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadJobs();
    
    const intervalId = setInterval(() => {
      setJobs(currentJobs => {
        const hasActiveJobs = currentJobs.some(job => 
          job.status === 'processing' || job.status === 'queued'
        );
        
        if (hasActiveJobs) {
          loadJobs(true);
        }
        
        return currentJobs;
      });
    }, 10000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleJobClick = async (job) => {
    if (job.status === 'completed') {
      try {
        const jobDetails = await getJobStatus(job.job_id);
        
        if (jobDetails.result && jobDetails.result.files) {
          const codeId = job._id || job.job_id;
          // Include title and requirements from the original job request
          const codeWithMetadata = {
            ...jobDetails.result,
            _id: codeId, // Add _id for favorite functionality
            title: job.request_payload?.title || '',
            requirements: job.request_payload?.requirements || '',
            language: job.request_payload?.language || jobDetails.result.language,
            framework: job.request_payload?.framework || jobDetails.result.framework
          };
          sessionStorage.setItem(`jobCode_${codeId}`, JSON.stringify({
            code: codeWithMetadata,
            jobId: job.job_id,
            codeId: job.code_id || null, // Pass the saved code_id if it exists
            savedToHistory: job.saved_to_history || false,
            needsApproval: true
          }));
          navigate(`/tools/code-generator/codes/${codeId}`);
        } else if (jobDetails.code_id) {
          navigate(`/tools/code-generator/codes/${jobDetails.code_id}`);
        } else {
          showError('Code data not found for this job');
        }
      } catch (error) {
        console.error('Failed to fetch job details:', error);
        showError('Failed to load code details');
      }
    } else if (job.status === 'failed') {
      const errorMsg = `Job failed: ${job.error || 'Unknown error'}`;
      showError(errorMsg);
    } else {
      showWarning(`Job is ${job.status}. Progress: ${job.progress || 0}%`);
    }
  };

  const handleDeleteJob = async (jobId, event) => {
    event.stopPropagation();
    setConfirmDialog({ 
      isOpen: true, 
      type: 'delete', 
      jobId: jobId,
      job: null 
    });
  };

  const confirmDeleteJob = async () => {
    const jobId = confirmDialog.jobId;
    setConfirmDialog({ isOpen: false, type: '', job: null, jobId: null });

    try {
      const response = await deleteJob(jobId);
      if (response.success) {
        setJobs(jobs.filter(job => job.job_id !== jobId));
        showSuccess('Job deleted successfully');
      } else {
        showError('Failed to delete job');
      }
    } catch (error) {
      console.error('Failed to delete job:', error);
      showError(`Failed to delete job: ${error.message}`);
    }
  };

  const handleRetryJob = async (job, event) => {
    event.stopPropagation();
    setConfirmDialog({ 
      isOpen: true, 
      type: 'retry', 
      job: job,
      jobId: null 
    });
  };

  const confirmRetryJob = async () => {
    const job = confirmDialog.job;
    setConfirmDialog({ isOpen: false, type: '', job: null, jobId: null });

    const payload = job.request_payload;
    
    if (!payload || (!payload.requirements)) {
      showError('Cannot retry: Job data is incomplete');
      return;
    }

    try {
      // Update job status immediately for UI feedback
      setJobs(currentJobs => currentJobs.map(j => 
        j.job_id === job.job_id 
          ? { ...j, status: 'queued', progress: 0, error: null }
          : j
      ));

      const response = await generateCode(payload);
      
      if (response.success && response.job_id) {
        showSuccess(`Job resubmitted successfully! Job ID: ${response.job_id}`);
        setTimeout(() => loadJobs(true), 1000);
      } else {
        showError('Failed to resubmit job');
        // Revert status on failure
        setTimeout(() => loadJobs(true), 500);
      }
    } catch (error) {
      console.error('Failed to retry job:', error);
      showError(`Failed to retry job: ${error.message}`);
      // Revert status on error
      setTimeout(() => loadJobs(true), 500);
    }
  };

  const copyJobId = (jobId, event) => {
    event.stopPropagation();
    
    const button = event.currentTarget;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(jobId)
        .then(() => {
          if (button) {
            const originalHTML = button.innerHTML;
            button.innerHTML = '✓';
            button.style.color = '#10b981';
            setTimeout(() => {
              button.innerHTML = originalHTML;
              button.style.color = '';
            }, 1500);
          }
          showSuccess('Job ID copied to clipboard');
        })
        .catch(err => {
          console.error('Failed to copy:', err);
          showError('Failed to copy Job ID: ' + err.message);
        });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = jobId;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        if (button) {
          const originalHTML = button.innerHTML;
          button.innerHTML = '✓';
          button.style.color = '#10b981';
          setTimeout(() => {
            button.innerHTML = originalHTML;
            button.style.color = '';
          }, 1500);
        }
        showSuccess('Job ID copied to clipboard');
      } catch (err) {
        console.error('Fallback copy failed:', err);
        showError('Failed to copy Job ID');
      }
      document.body.removeChild(textArea);
    }
  };

  const capitalize = (str) => {
    if (!str) return '';
    // Special cases
    const specialCases = {
      'python': 'Python',
      'typescript': 'TypeScript',
      'javascript': 'JavaScript',
      'java': 'Java',
      'csharp': 'C#',
      'go': 'Go'
    };
    return specialCases[str.toLowerCase()] || str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleRefresh = () => {
    loadJobs(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'processing':
        return '⚙️';
      case 'queued':
        return '⏳';
      default:
        return '❓';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'processing':
        return 'status-processing';
      case 'queued':
        return 'status-queued';
      default:
        return 'status-unknown';
    }
  };

  const calculateDuration = (job, isFailed = false) => {
    const startTime = job.retried_at || job.created_at;
    const endTime = isFailed ? job.updated_at : job.completed_at;
    
    if (!startTime || !endTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    
    // Handle invalid dates or negative duration
    if (isNaN(durationMs) || durationMs < 0) return 'N/A';
    
    // Cap all durations at 15 minutes (900000 ms)
    // Lambda max execution time is 15 minutes
    // If it shows longer, it's a timestamp issue with old jobs
    const maxDuration = 900000; // 15 minutes
    const cappedDurationMs = Math.min(durationMs, maxDuration);
    
    // If duration was capped significantly (more than actual duration), show indicator
    const wasCapped = durationMs > maxDuration;
    
    const seconds = Math.floor(cappedDurationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    let timeStr;
    if (hours > 0) {
      timeStr = `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      timeStr = `${minutes}m ${seconds % 60}s`;
    } else {
      timeStr = `${seconds}s`;
    }
    
    // Add indicator if duration was capped
    return wasCapped ? `~${timeStr}` : timeStr;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let dateStr = timestamp;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('T')) {
      dateStr = timestamp + 'Z';
    } else if (timestamp.includes('T') && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
      dateStr = timestamp + 'Z';
    }
    
    const date = new Date(dateStr);
    
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  if (loading && !refreshing) {
    return (
      <div className="code-jobs-list-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="code-jobs-list-container">
      <div className="code-jobs-header">
        <h2>⚙️ Code Generation Jobs</h2>
        <button 
          className="btn-refresh"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          🔄 {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button className="btn-retry" onClick={() => loadJobs()}>
            Retry
          </button>
        </div>
      )}

      {!error && jobs.length === 0 && (
        <div className="no-jobs">
          <p>📭 No jobs found</p>
          <p className="hint">Generate code to see jobs here</p>
        </div>
      )}

      {!error && jobs.length > 0 && (
        <div className="code-jobs-table-container">
          <table className="code-jobs-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Title / Requirements</th>
                <th>Language & Framework</th>
                <th>Duration</th>
                <th>Started</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr 
                  key={job.job_id} 
                  className={`job-row ${getStatusClass(job.status)}`}
                >
                  <td className="status-cell">
                    <span className="status-badge">
                      {getStatusIcon(job.status)} {job.status}
                    </span>
                  </td>
                  <td className="requirements-cell" title={job.request_payload?.title || job.request_payload?.requirements || 'N/A'}>
                    {job.request_payload?.title || 
                     ((job.request_payload?.requirements || 'N/A').substring(0, 80) + 
                      ((job.request_payload?.requirements || '').length > 80 ? '...' : ''))}
                  </td>
                  <td className="config-cell">
                    <div className="config-info">
                      <span className="language-badge">
                        💻 {capitalize(job.request_payload?.language) || 'N/A'}
                      </span>
                      {job.request_payload?.framework && (
                        <span className="framework-badge">
                          🔧 {job.request_payload.framework}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="duration-cell">
                    <span className="duration-text">
                      {job.status === 'completed'
                        ? calculateDuration(job, false)
                        : job.status === 'failed'
                        ? calculateDuration(job, true)
                        : '⏳ In progress'}
                    </span>
                  </td>
                  <td className="timestamp-cell">
                    {formatTimestamp(job.created_at)}
                  </td>
                  <td className="action-cell">
                    <div className="action-buttons">
                      <button
                        className={`btn-view ${job.status === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleJobClick(job)}
                        disabled={job.status === 'processing' || job.status === 'queued'}
                      >
                        {job.status === 'completed' ? '📄 View Code' : 
                         job.status === 'failed' ? '❌ Details' : 
                         '⏳ In Progress'}
                      </button>
                      {job.status === 'failed' && (
                        <button
                          className="btn-retry"
                          onClick={(e) => handleRetryJob(job, e)}
                          title="Retry this job"
                        >
                          ↻
                        </button>
                      )}
                      <button
                        className="btn-copy-action"
                        onClick={(e) => copyJobId(job.job_id, e)}
                        title={`Copy Job ID: ${job.job_id}`}
                      >
                        📋
                      </button>
                      <button
                        className="btn-delete"
                        onClick={(e) => handleDeleteJob(job.job_id, e)}
                        title="Delete job"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="code-jobs-footer">
        <p className="auto-refresh-note">
          🔄 Auto-refreshing every 10 seconds
        </p>
        <p className="jobs-count">
          Total Jobs: {jobs.length} | 
          Completed: {jobs.filter(j => j.status === 'completed').length} | 
          Processing: {jobs.filter(j => j.status === 'processing' || j.status === 'queued').length} | 
          Failed: {jobs.filter(j => j.status === 'failed').length}
        </p>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.type === 'retry' ? 'Retry Job' : 'Delete Job'}
        message={confirmDialog.type === 'retry' 
          ? 'Do you want to resubmit this job with the same parameters?' 
          : 'Are you sure you want to delete this job? This action cannot be undone.'}
        type={confirmDialog.type === 'retry' ? 'warning' : 'danger'}
        confirmText={confirmDialog.type === 'retry' ? 'Retry' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDialog.type === 'retry' ? confirmRetryJob : confirmDeleteJob}
        onCancel={() => setConfirmDialog({ isOpen: false, type: '', job: null, jobId: null })}
      />
    </div>
  );
}

export default CodeJobsList;
